/**
 * Numbered step rail for the multi-step creation flows.
 *
 * Shows where you are and what each step is for. Steps are not clickable: the flows validate
 * forward, so jumping to step 3 before step 1 is filled has no meaning.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';

export interface Step {
  /** 1-based. */
  number: number;
  title: string;
  hint: string;
}

const navStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
  flex: 1 1 200px;
  max-width: 240px;
  min-width: 180px;
`;

const stepStyle = css`
  display: flex;
  gap: var(--space-sm);
`;

const bulletStyle = css`
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
`;

const bulletActiveStyle = css`
  background: var(--color-primary);
  color: var(--color-on-primary);
  border-color: var(--color-primary);
`;

const bulletDoneStyle = css`
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  border-color: var(--color-primary);
`;

const textStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const titleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const titleActiveStyle = css`
  color: var(--color-primary);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface StepNavProps {
  steps: Step[];
  /** 1-based number of the step being shown. */
  currentStep: number;
}

export const StepNav = ({ steps, currentStep }: StepNavProps): JSX.Element => (
  <nav className={navStyle} aria-label='Etapas'>
    {steps.map(step => {
      const isActive = step.number === currentStep;
      const isDone = step.number < currentStep;

      return (
        <div key={step.number} className={stepStyle}>
          <span
            className={`${bulletStyle} ${isActive ? bulletActiveStyle : ''} ${
              isDone ? bulletDoneStyle : ''
            }`}
            aria-current={isActive ? 'step' : undefined}
          >
            {step.number}
          </span>
          <div className={textStyle}>
            <span className={`${titleStyle} ${isActive ? titleActiveStyle : ''}`}>
              {step.title}
            </span>
            <span className={hintStyle}>{step.hint}</span>
          </div>
        </div>
      );
    })}
  </nav>
);
